import React from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, CircleCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { fmtPrice } from "@/lib/api";

export default function MiniCart() {
  const { items, miniOpen, closeMini, setQty, subtotal, lastAddedSku } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();

  const go = (path) => { closeMini(); navigate(path); };

  return (
    <AnimatePresence>
      {miniOpen && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-label={t("mini_added")}>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeMini}
            className="absolute inset-0 bg-olive-deep/45 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="mini-cart"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 bottom-0 flex max-h-[82vh] flex-col rounded-t-3xl bg-ivory shadow-2xl sm:inset-y-0 sm:left-0 sm:right-auto sm:max-h-none sm:w-[420px] sm:rounded-none"
          >
            <div className="flex items-center justify-between border-b border-olive/10 px-5 py-4">
              <div className="flex items-center gap-2 text-olive-dark">
                <CircleCheck className="h-5 w-5 text-olive" />
                <span className="font-display font-bold">{t("mini_added")}</span>
              </div>
              <button data-testid="mini-cart-close" onClick={closeMini} aria-label="إغلاق"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-olive/5 text-ink/60 transition-colors hover:bg-olive/10">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {items.map((i) => (
                <div key={i.sku} data-testid={`mini-cart-item-${i.sku}`}
                  className={`flex items-center gap-3 rounded-2xl border p-2.5 transition-colors ${i.sku === lastAddedSku ? "border-olive/30 bg-olive/[0.05]" : "border-olive/10 bg-white"}`}>
                  <img src={i.image} alt={i.name_ar} loading="lazy" decoding="async" className="h-16 w-14 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-sm font-semibold text-ink">{i.name_ar}</div>
                    <div className="text-sm text-olive">{fmtPrice(i.price)}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setQty(i.sku, i.qty - 1)} aria-label="إنقاص"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-olive/8 text-olive-dark transition-colors hover:bg-olive/15">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                    <button onClick={() => setQty(i.sku, i.qty + 1)} aria-label="زيادة"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-olive/8 text-olive-dark transition-colors hover:bg-olive/15">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-olive/10 p-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-ink/60">{t("subtotal")}</span>
                <span data-testid="mini-cart-subtotal" className="font-display text-lg font-bold text-olive-dark">{fmtPrice(subtotal)}</span>
              </div>
              <div className="flex flex-col gap-2.5">
                <button data-testid="mini-cart-checkout" onClick={() => go("/checkout")}
                  className="btn-shine w-full rounded-full bg-olive py-3.5 font-semibold text-ivory transition-colors hover:bg-olive-dark">
                  {t("mini_checkout")}
                </button>
                <div className="flex gap-2.5">
                  <button data-testid="mini-cart-continue" onClick={closeMini}
                    className="flex-1 rounded-full border border-olive/25 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
                    {t("mini_continue")}
                  </button>
                  <button data-testid="mini-cart-view-cart" onClick={() => go("/cart")}
                    className="flex-1 rounded-full border border-olive/25 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
                    {t("mini_view_cart")}
                  </button>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
